import React, { useRef, useState, useEffect } from 'react';
import { isEmpty } from 'lodash-es';
import { PC_CONSOLE_COMBINE_ENTITY_TYPE } from '@intelligence/pages/TopCharts/_config/topChartsConst';
import type { IGameType } from '@/pages/intelligence/types/common';
import { useParams, useLocation } from 'umi';
import { FROM_PREFIX } from '@intelligence/components/UserGuide/utils';
import { formatDateStrPlus, getNullableMoment, getDatesByDateRange } from '@intelligence/v3/utils/date';

const DAILY_DATE_TYPES = ['date', 'day', 'daily'];
const FIRST_WEEK_SINCE_RELEASE_SHORTCUT = 'First Week Since Release';

export const isFirstWeekSinceReleaseShortcut = (shortcutType?: string): boolean => {
  return shortcutType === FIRST_WEEK_SINCE_RELEASE_SHORTCUT;
};

export const getFirstWeekSinceReleaseDates = (params: {
  dateType?: string;
  startDate?: string;
  endDate?: string;
  releaseDate?: string;
}): string[] => {
  const { dateType, startDate, endDate, releaseDate } = params || {};

  if (!DAILY_DATE_TYPES.includes(dateType?.toLocaleLowerCase() || '')) {
    return [];
  }

  const releaseDateMoment = getNullableMoment(releaseDate);
  const startDateMoment = getNullableMoment(startDate);
  const endDateMoment = getNullableMoment(endDate);
  if (!releaseDateMoment || !startDateMoment || !endDateMoment) {
    return [];
  }

  const firstWeekEndDate = releaseDateMoment.clone().add(6, 'days');
  const isFirstWeekSinceRelease = startDateMoment.isSame(releaseDateMoment, 'day') && endDateMoment.isSame(firstWeekEndDate, 'day');
  if (!isFirstWeekSinceRelease) {
    return [];
  }

  return getDatesByDateRange({
    granularity: 'daily',
    start_date: formatDateStrPlus(releaseDateMoment),
    end_date: formatDateStrPlus(firstWeekEndDate),
  });
};

export const enableTabsMobile = [
  'overview',
  'stats',
  'countries',
  'storeRank',
  'streaming',
  'demographics',
  'overlap',
  'gameComparison',
  'opinions',
];
export const enableTabs = [
  'overview',
  'stats',
  'countries',
  'overlap',
  'gameComparison',
  'opinions',
  'storeRank',
  'streaming',
];
export const enableTabsConsole = [
  'overview',
  'stats',
  'countries',
  'gameComparison',
  'opinions',
  'storeRank',
  'streaming',
];
export const enableTabsCombine = [
  'gameInfo',
  'overview',
  'KPIOverview',
  'KPITrends',
  'stats',
  'originalStats',
  'countries',
  'storeRank',
  'streaming',
  'overlap',
  'gameComparison',
  'opinions',
];

export const NEWTRUEDATAFEEDBACK = [
  'mobile',
  'combine',
];

export const enableTabsMap: {
  [key in IGameType]: string[];
} = {
  mobile: enableTabsMobile,
  pc: enableTabs,
  console: enableTabsConsole,
  [PC_CONSOLE_COMBINE_ENTITY_TYPE]: enableTabsCombine,
};

export const newMarkIdsMappingKeys: {
  [key: string]: string;
} = {
  mobile: 'MobileGamesDetails',
  pc: 'PCGamesDetails',
  console: 'ConsoleGamesDetails',
  [PC_CONSOLE_COMBINE_ENTITY_TYPE]: 'PCConsoleGamesDetails',
};

export const getValidTab = (payload: string, entityType: IGameType): string => {
  const tabs = enableTabsMap?.[entityType] || enableTabs;
  return tabs.includes(payload) ? payload : enableTabs[0];
};

export function useActiveIdState(
  entityType: IGameType,
  tab: string,
): [string, React.Dispatch<React.SetStateAction<string>>] {
  const [activeId, setActiveId] = useState(() => getValidTab(tab, entityType));

  useEffect(() => {
    setActiveId(getValidTab(tab, entityType));
  }, [entityType, tab]);

  return [activeId, setActiveId];
}

// tab页签更新后，页面主体回滚到顶部
export function useResetPositionWhenTabChange(activeId: string, gameId: string) {
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = container.current?.querySelector('.tea-layout__content-inner');
    node?.scrollIntoView({
      block: 'start',
      // 这里不要开启动画效果，动画效果会导致游戏详情页头部有轻微卡顿的现象
      // behavior: 'smooth',
    });
  }, [activeId, gameId]);

  return container;
}

/**
 * Competitor新手引导需要用到，主要用于 监听url变化，更新页签、segment状态
 */
export function useUserGuideCompetitorHelper(
  entityType: IGameType,
  props: {
    activeId: string;
    setActiveId: React.Dispatch<React.SetStateAction<string>>;
    activeSegmentId: string;
    setActiveSegmentId: React.Dispatch<React.SetStateAction<string>>;
  },
) {
  const { activeId, setActiveId, activeSegmentId, setActiveSegmentId } = props;

  const location = useLocation();
  const { tab, segment } = useParams<any>();
  const urlSearchParams = new URLSearchParams(location.search.substring(1));
  const from = urlSearchParams.get('from') || '';

  useEffect(() => {
    const reg = new RegExp(`^${FROM_PREFIX}_(\\\d+)$`, 'g');
    if (!reg.test(from)) return;
    if (tab === activeId && segment === activeSegmentId) return;
    const validTab = getValidTab(tab, entityType);
    setActiveId(validTab);
    setActiveSegmentId(segment);
  }, [from, tab, segment]);
}

// 融合游戏KPIOverview页面公共参数
export const commonKPIOverviewParams = {
  entity_type: PC_CONSOLE_COMBINE_ENTITY_TYPE,
  data_sources: [],
  is_first: false,
  region_type: 'region',
  regions: ['global'],
  is_change_region: false,
  is_new_sytle: true,
};

// 校正url输入的是不是页面可读取的，不是返回默认值
export const checkValidQuery = (urlAppidList: any, appIdList: string[]) => {
  try {
    const currentAppid: string[] = [];
    urlAppidList?.forEach((id: string) => {
      if (appIdList?.includes(id)) {
        currentAppid.push(id);
      }
    });
    return currentAppid.length ? currentAppid : appIdList;
  } catch (err) {
    return appIdList;
  }
};

/**
 * 判断Overview卡片是否无数据
 * @param data Overview卡片数据
 * @reutrn Overview卡片是否无数据
 */
export const calcIsEmptyOverviewCard = (data?: ISteamOverviewCardData | null): boolean => {
  if (!data) return true;
  const basicData = data?.baseIndicators || {};
  const metricData = data?.numArrowData || {};
  const rankData = data?.rankData || {};
  const lineData = data?.lineData || {};

  const metricValue = basicData?.indicatorsVal;
  const metricNumContent = metricData?.numContent;

  const rankValue = basicData?.rankVal;
  const rankNumContent = rankData?.numContent;

  const isMetricValueNoData = !metricValue && !metricNumContent;
  const isRankValueNoData = (rankValue === 0 || Number.isNaN(Number.parseInt(rankValue as any, 10))) && !rankNumContent;
  const isLineValueNoData =
    isEmpty(lineData?.seriesData) && isEmpty(lineData?.xAxisData) && isEmpty(lineData?.weekDate);

  return isMetricValueNoData && isRankValueNoData && isLineValueNoData;
};
